import { FormGroup, FormBuilder, FormArray, FormControl } from '@angular/forms';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { PackagingSystemApiService } from '../../../../data-transfer/services/packaging-system-api-service';
import { PackagingSystemDto } from './../../../../data-transfer/entities/packaging-system-entities/packaging-system-dto';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { PackagingSystemNavigationService } from 'src/app/navigation/services/navigation-services/packaging-system-navigation.service';
import { forkJoin, of } from 'rxjs';
import { PermissionTypeDto } from 'src/app/data-transfer/entities/permission-type-dto';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DeleteDialogData, DeleteItemDialogComponent } from 'src/app/components/dialogs/delete-item-dialog/delete-item-dialog.component';
import { DeletedEntitiesDto } from 'src/app/data-transfer/entities/deleted-entities-dto';
import { DirectoryDto } from 'src/app/data-transfer/entities/directory-dto';
import { SelectLocationDialogComponent } from '../../dialogs/select-location-dialog/select-location-dialog.component';
import { PackagingSystemInfoDto } from 'src/app/data-transfer/entities/packaging-system-entities/packaging-system-info-dto';
import { DialogActions } from 'src/app/model/dictionary';
import { HistoryWrapperLifeCycle, HistoryWrapperLifeCycleCradleToGate } from 'src/app/data-transfer/entities/evaluation-entities/history-wrapper-life-cycle';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { SimpleConfirmDialogComponent } from 'src/app/components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { HistoryWrapperRecyclability } from 'src/app/data-transfer/entities/evaluation-entities/history-wrapper-recyclability';
import { ComparisonDataService } from 'src/app/navigation/services/comparison-data-service';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { EditQuantitiesDialogComponent } from '../quantities/edit-quantities-dialog/edit-quantities-dialog.component';
import { EditQuantityDialogComponent } from '../quantities/edit-quantity-dialog/edit-quantity-dialog.component';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { PaginationFilterDto } from 'src/app/data-transfer/entities/pagination-filter-dto';
import { Sort } from '@angular/material/sort';
import { PackagingSystemPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { DataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { PpwrApiService } from 'src/app/data-transfer/services/ppwr-api.service';
import { CreateDocRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-request-dto';
import { CreateDocTdRequestDto } from 'src/app/data-transfer/entities/doc-entities/create-doc-td-request';
import { OverviewTableComponent } from 'src/app/components/shared-components/parent-components/overview-table/overview-table.component';

@Component({
  selector: 'app-packaging-systems-overview',
  templateUrl: './packaging-systems-overview.component.html',
  styleUrls: ['./packaging-systems-overview.component.scss'],
})
export class PackagingSystemsOverviewComponent extends OverviewTableComponent implements OnInit, OnChanges, AfterViewInit {
  private _dataSource!:
    | PaginationDataSource<PackagingSystemInfoDto>
    | PaginationDataSource<PackagingSystemDto>
    | MatTableDataSource<PackagingSystemInfoDto>
    | MatTableDataSource<PackagingSystemDto>;

  @Input()
  set dataSource(value: typeof this._dataSource) {
    this._dataSource = value;
    this.isPaginatorVisible = value instanceof PaginationDataSource;
  }
  get dataSource() {
    return this._dataSource;
  }

  @Input() displayedColumns: string[] = [];
  @Input() subRequestMap?: Map<number, DataExchangeSubRequestDto>;
  @Input() commentCountMap?: Map<number, number>;
  @Input() public analysisWrappersRec?: HistoryWrapperRecyclability[];
  @Input() public analysisWrappersLca?: HistoryWrapperLifeCycle[];
  @Input() public analysisWrappersLcaCradleToGate?: HistoryWrapperLifeCycleCradleToGate[];
  @Input() isUserValidator = false;
  @Input() isRecyclingBinSelected = false;
  @Input() sortByColumnName?: string;
  @Input() form: FormGroup = this.formBuilder.group({ quantityForms: this.formBuilder.array([]) });
  @Input() directoryIds?: number[];

  @Output() dataChanged = new EventEmitter();
  @Output() distributionCountryAdded = new EventEmitter();
  @Output() infoButtonClick = new EventEmitter<PackagingSystemDto | PackagingSystemInfoDto>();
  @Output() navigateToSubRequest = new EventEmitter<number>();
  @Output() approveSubRequest = new EventEmitter<number>();
  @Output() reopenSubRequest = new EventEmitter<number>();
  @Output() withdrawSubRequest = new EventEmitter<number>();
  @Output() showSubRequestComments = new EventEmitter<number>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private packagingSystemIds: number[] = [];
  selectedPackagingSystemIds: number[] = [];
  callerLevelId = PackagingPart.System;
  isLoading = of(false);

  suppliers: string[] = [];
  directories: { name: string; value: number }[] = [];
  isPaginatorVisible = false;

  constructor(
    private packagingSystemApiService: PackagingSystemApiService,
    private formBuilder: FormBuilder,
    private analysisApiService: AnalysisApiService,
    private comparisonDataService: ComparisonDataService,
    protected navigationService: PackagingSystemNavigationService,
    protected directoryApiService: DirectoryApiService,
    protected importExportApiService: ImportExportApiService,
    protected dialog: MatDialog,
    protected translateService: TranslocoService,
    protected userApiService: UserApiService,
    protected ppwrApiService: PpwrApiService,
    protected spinner: NgxSpinnerService,
    @Inject(LOCALE_ID) public locale: string,
    private dataExchangeRequestApiService: DataExchangeRequestApiService,
    private cd: ChangeDetectorRef
  ) {
    super(dialog, directoryApiService, importExportApiService, navigationService, translateService, spinner, locale);
    this.dataSource = new MatTableDataSource<PackagingSystemInfoDto>();
  }

  get quantityForms(): FormArray {
    return this.form.controls.quantityForms as FormArray;
  }

  ngOnInit(): void {
    if (this.isUserValidator) {
      this.displayedColumns.push('validation');
      this.displayedColumns.push('organizationId');
      this.displayedColumns.push('creationTimestamp');
      this.organizationsSubscription = this.userApiService.getOrganizations().subscribe(organizations => (this.organizations = organizations));
    }

    if (this.displayedColumns.includes('supplier')) {
      this.packagingSystemApiService.getSuppliers().subscribe(x => (this.suppliers = x));
    }

    if (this.displayedColumns.includes('directoryName')) {
      this.directoryApiService.getDirectories().subscribe(dir => (this.directories = this.flattenDirectories(dir)));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dataSource instanceof PaginationDataSource) {
      this.ngOnChangesPagination(changes);
    } else {
      this.ngOnChangesStatic(changes, this.dataSource);
    }
  }

  private ngOnChangesPagination(changes: SimpleChanges): void {
    if (changes.dataSource != null) {
      this.selectedPackagingSystemIds = [];
    }
    if ((changes.directoryIds || changes.isRecyclingBinSelected) && this.paginator) {
      this.refreshDataSource();
      return;
    }
  }

  private ngOnChangesStatic(changes: SimpleChanges, dataSource: MatTableDataSource<PackagingSystemDto> | MatTableDataSource<PackagingSystemInfoDto>): void {
    if (changes.dataSource != null) {
      super.setDataSourceFilter(dataSource);
      this.updateDisplayTrackedColumn(dataSource);
      this.dataSourceSubscription = dataSource.connect().subscribe(_ => {
        this.updateDisplayTrackedColumn(dataSource);
      });
      this.selectedPackagingSystemIds = [];
    }
  }

  private refreshDataSource() {
    if (!(this.dataSource instanceof PaginationDataSource)) {
      return;
    }

    if (!this.isRecyclingBinSelected && this.directoryIds != null && this.directoryIds.length === 0) {
      this.dataSource.clear();
      return;
    }

    let filters: PaginationFilterDto[] = [];

    if (!this.isRecyclingBinSelected && this.directoryIds && this.directoryIds.length > 0) {
      filters = this.directoryIds.map(id => ({ fieldName: 'DirectoryId', value: id.toString() }));
    }

    this.dataSource.fetchData(this.paginator.pageIndex, this.paginator.pageSize, filters);
  }

  ngAfterViewInit() {
    if (this.dataSource instanceof PaginationDataSource) {
      this.ngAfterViewInitPagination(this.dataSource);
    } else {
      this.ngAfterViewInitStatic(this.dataSource);
    }
  }

  private ngAfterViewInitPagination(dataSource: PaginationDataSource<PackagingSystemDto> | PaginationDataSource<PackagingSystemInfoDto>): void {
    this.refreshDataSource();
    dataSource.page.subscribe(page => {
      this.paginator.length = page.totalItemCount;
      this.paginator.pageIndex = page.pageIndex;
      this.packagingSystemIds = page.allIds;
      this.selectedPackagingSystemIds = this.selectedPackagingSystemIds.filter(id => page.allIds.includes(id));
    });
    dataSource.filteredColumns.subscribe(filteredColumns => {
      this.filteredColumns = filteredColumns;
    });
    this.cd.detectChanges();
  }

  private ngAfterViewInitStatic(dataSource: MatTableDataSource<PackagingSystemInfoDto> | MatTableDataSource<PackagingSystemDto>) {
    dataSource.sort = this.sort;
    dataSource.sortingDataAccessor = (data: PackagingSystemDto, sortHeaderLabel: string) => {
      let value;
      if (sortHeaderLabel === 'quantityInput') {
        value = this.form.value.quantityForms.find((quantityEntry: any) => quantityEntry.id === data.id).quantity;
      } else if (sortHeaderLabel === 'analysisId') {
        value = this.getAnalysisIdForPackaging(data.id);
      } else if (sortHeaderLabel === 'subRequestStatus') {
        value = this.translateService.translate(this.getSubRequestStatusLabel(this.subRequestMap?.get(data.id)?.status));
      } else {
        value = data[sortHeaderLabel as keyof PackagingSystemDto];
      }
      return typeof value === 'string' ? value.toLowerCase() : value;
    };
  }

  protected getFilterValue(data: PackagingSystemDto | PackagingSystemInfoDto, key: string): any {
    if (key === 'subRequestStatus') {
      return this.translateService.translate(this.getSubRequestStatusLabel(this.subRequestMap?.get(data.id)?.status));
    }

    return super.getFilterValue(data, key);
  }

  navigateToPackagingSystem(packagingSystem: PackagingSystemDto) {
    if (packagingSystem.id == null) {
      return;
    }
    this.navigationService.navigateToPackagingSystem(packagingSystem.id);
  }

  private openDeleteDialog(packagingSystems: number[]) {
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translateService.translate('packagingSystem.deletePackagingSystemHeader'),
      dialogText: this.translateService.translate('packagingSystem.deletePackagingSystemText'),
    };
    const dialogRef = this.dialog.open(DeleteItemDialogComponent, getDialogConfig(dialogData, '500px'));
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.DELETE) {
        this.deletePackagingSystems(packagingSystems);
      }
    });
  }

  emptyRecyclingBin() {
    if (!this.isRecyclingBinSelected) {
      console.log('PackagingSystemsOverviewComponent: Recycling bin must be selected prior to emptying.');
      return;
    }
    this.deletePackagingSystems(this.dataSource.data.map(x => x.id));
  }

  private refreshDataSourceAfterChange(filterPredicate: (value: PackagingSystemInfoDto) => boolean | undefined) {
    if (this.dataSource instanceof PaginationDataSource) {
      this.refreshDataSource();
    } else {
      this.dataSource.data = this.dataSource.data.filter(filterPredicate);
    }
  }

  private deletePackagingSystems(packagingSystems: number[]) {
    this.spinner.show();
    const deletePermamently = this.isRecyclingBinSelected;
    const toDeleteDto: DeletedEntitiesDto = {
      deleted: packagingSystems.map(id => ({ id: id ?? -1, version: 0 })),
    };
    this.deleteBackendSubscription = this.packagingSystemApiService.deletePackagingSystems(toDeleteDto, deletePermamently, true).subscribe({
      next: _ => {
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getDeleteSuccessData(), '500px'));
        this.refreshDataSourceAfterChange(value => {
          if (value.id != null) {
            return !toDeleteDto.deleted.map(x => x.id).includes(value.id);
          }
        });
        this.spinner.hide();
      },
      error: e => {
        let errorData;
        if (e.status === 403) {
          errorData = this.getDeletionImpossibleData();
        } else {
          errorData = this.getDeleteErrorData();
        }
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(errorData, '500px'));
        this.spinner.hide();
      },
    });
  }

  selectAllPackagingSystems() {
    this.allItemsSelected = !this.allItemsSelected;
    this.selectedPackagingSystemIds = [];
    if (this.allItemsSelected) {
      for (const packagingSystemId of this.packagingSystemIds) {
        this.selectedPackagingSystemIds.push(packagingSystemId);
      }
    }
    this.packagingPartsSelected.emit(this.selectedPackagingSystemIds);
  }

  isPackagingSystemSelected(item: PackagingSystemInfoDto): boolean {
    if (this.selectedPackagingSystemIds && this.selectedPackagingSystemIds.length > 0) {
      return this.selectedPackagingSystemIds.includes(item.id);
    }
    return false;
  }

  setPackagingSystemSelected(checked: boolean, item: PackagingSystemInfoDto) {
    if (checked) {
      this.selectedPackagingSystemIds.push(item.id);
    } else {
      const index = this.selectedPackagingSystemIds.indexOf(item.id);
      this.selectedPackagingSystemIds.splice(index, 1);
    }
    this.packagingPartsSelected.emit(this.selectedPackagingSystemIds);
  }

  movePackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]) {
    const data = { rootFolder, unreachableFolders };
    const dialogConfig = getDialogConfig(data, '500px');
    const dialogRef = this.dialog.open(SelectLocationDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.REJECT) {
        return;
      }
      const observables = this.selectedPackagingSystemIds.map(packagingSystemId => {
        if (packagingSystemId != null) {
          return this.directoryApiService.movePackagingSystem(result.data.id, packagingSystemId);
        }
        return of(null);
      });
      this.moveBackendSubscription = forkJoin(observables).subscribe(_ => {
        this.dataChanged.emit();
        this.refreshDataSourceAfterChange(value => this.selectedPackagingSystemIds.includes(value.id ?? -1));
      });
    });
  }

  copyPackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]) {
    const data = { rootFolder, unreachableFolders };
    const dialogConfigFolderSelection = getDialogConfig(data, '500px');
    const dialogRefFolderSelection = this.dialog.open(SelectLocationDialogComponent, dialogConfigFolderSelection);
    this.dialogSubscription = dialogRefFolderSelection.afterClosed().subscribe(foldersResult => {
      if (foldersResult.event === DialogActions.REJECT) {
        this.spinner.hide();
        return;
      }
      const observables = this.selectedPackagingSystemIds.map(packagingSystemId => {
        if (packagingSystemId != null) {
          return this.directoryApiService.copyPackagingSystem(foldersResult.data.id, packagingSystemId);
        }
        return of(null);
      });
      this.copyBackendSubscription = forkJoin(observables).subscribe({
        next: _ => this.dataChanged.emit(),
        error: _ => this.spinner.hide(),
      });
    });
  }

  deletePackagingPart(itemsToDelete = this.selectedPackagingSystemIds) {
    const observables = itemsToDelete
      .filter(packagingId => packagingId != null)
      .map(packagingId => {
        return this.packagingSystemApiService.getPackagingSystemPermissions(packagingId);
      });
    this.permissionsSubscription = forkJoin(observables).subscribe((permissionsList: PermissionTypeDto[]) => {
      if (permissionsList.filter(x => x.delete === false).length > 0) {
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getDeleteNotAllowedData(this.translateService), '500px'));
      } else {
        this.openDeleteDialog(itemsToDelete);
      }
    });
  }

  exportPackagingPart() {
    this.spinner.show();
    const observables = this.selectedPackagingSystemIds.map(packagingSystemId => {
      return this.analysisApiService.isRecyclabilityAnalysisPossible(packagingSystemId, true, PackagingPart.System);
    });
    this.recSubscription = forkJoin(observables).subscribe(returnList => {
      const proceedWithExport = !returnList.includes(false);

      const dialogConfig = getDialogConfig({ packagingSystemIds: this.selectedPackagingSystemIds }, '50%');

      if (proceedWithExport) {
        this.doExport(dialogConfig);
      } else {
        const data: SimpleDialogData = {
          title: this.translateService.translate('common.text.warning'),
          messages: [this.translateService.translate('warnings.exportIncompletePackagingSystem')],
          icon: 'warning',
        };
        const confirmDialogConfig = getDialogConfig(data, '350px');
        const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, confirmDialogConfig);
        this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
          if (result.event === DialogActions.REJECT) {
            this.spinner.hide();
            return;
          } else {
            this.doExport(dialogConfig);
          }
        });
      }
    });
  }

  private doExport(dialogConfig: MatDialogConfig) {
    const exportProfilesFunction = () => this.importExportApiService.getPackagingSystemExportProfiles();
    const exportFunction = (packagingId: number, principalId: number, profileId?: number, subprofileId?: number) =>
      this.importExportApiService.exportPackagingSystem(packagingId, principalId, profileId, subprofileId, true);
    super.exportItems(dialogConfig, exportProfilesFunction, exportFunction, true);
    this.resetSelection();
  }

  createDataExchangeRequest(): void {
    this.openCreateDataExchangeRequestDialog({ allPrincipals: [], packagingSystemIds: this.selectedPackagingSystemIds }, dto =>
      this.dataExchangeRequestApiService.createDataExchangeRequest(dto, true)
    );
    this.resetSelection();
  }

  restorePackagingPart() {
    this.restorePackagingSystems(this.selectedPackagingSystemIds);
  }

  restoreAllPackagingSystems() {
    this.restorePackagingSystems(this.dataSource.data.map(x => x.id));
  }

  getFormControlForQuantity(element: PackagingSystemDto) {
    return (this.quantityForms.controls.find(control => control.value.id === element.id) as FormGroup).controls.quantity as FormControl;
  }

  addCountryToPackaging(packagingUnit: PackagingSystemDto) {
    this.distributionCountryAdded.emit({ id: packagingUnit.id, version: packagingUnit.version });
  }

  private restorePackagingSystems(packagingSystems: number[]) {
    if (!this.isRecyclingBinSelected) {
      console.log('PackagingSystemsOverviewComponent: Recycling bin must be selected prior to restoring.');
      return;
    }
    for (const packagingSystemId of packagingSystems) {
      this.refreshDataSourceAfterChange(value => {
        return value.id !== packagingSystemId;
      });
      if (packagingSystemId != null) {
        this.restoreSubscription = this.directoryApiService.restorePackagingSystemFromRecyclingBin(packagingSystemId).subscribe(_ => {
          window.location.reload();
        });
      }
    }
  }

  getAnalysisWrappersRecForPackaging(packagingSystemId: number) {
    return this.analysisWrappersRec?.find(x => x.id === packagingSystemId) ?? undefined;
  }

  getAnalysisWrappersLcaForPackaging(packagingSystemId: number) {
    return this.analysisWrappersLca?.find(x => x.id === packagingSystemId) ?? undefined;
  }

  getAnalysisIdForPackaging(packagingSystemId: number) {
    if (this.analysisWrappersRec) {
      return this.analysisWrappersRec?.find(x => x.id === packagingSystemId)?.analysisId ?? undefined;
    } else {
      return this.analysisWrappersLca?.find(x => x.id === packagingSystemId)?.analysisId ?? undefined;
    }
  }

  getAnalysisWrappersLcaCradleToGateForPackaging(packagingSystemId: number) {
    return this.analysisWrappersLcaCradleToGate?.find(x => x.id === packagingSystemId) ?? undefined;
  }

  comparePackagingPart() {
    const observablesPermissions = this.selectedPackagingSystemIds
      .filter(packagingSystemId => packagingSystemId != null)
      .map(packagingSystemId => {
        return this.packagingSystemApiService.getPackagingSystemPermissions(packagingSystemId);
      });
    this.permissionsSubscription = forkJoin(observablesPermissions).subscribe((permissionsList: PermissionTypeDto[]) => {
      const isComparisonPermitted = permissionsList.map(x => x.analyze).find(x => x === false) === undefined;
      if (!isComparisonPermitted) {
        this.navigationService.stopWhenAnalysisNotPermitted();
        return;
      }
      const listOfIds: number[] = this.selectedPackagingSystemIds.filter(id => id != null).map(id => id ?? -1);
      this.comparisonDataService.setPackagingVersionMapping(this.selectedPackagingSystemIds.map(id => id));

      this.navigationService.navigateToComparison(listOfIds);
    });
  }

  editPackagingPartQuantity() {
    const dialogConfig = getDialogConfig({ packagingSystemIds: this.selectedPackagingSystemIds }, '900px');
    const dialogType: any = this.selectedPackagingSystemIds.length > 1 ? EditQuantitiesDialogComponent : EditQuantityDialogComponent;
    this.dialog.open(dialogType, dialogConfig);
  }

  getTagsSettingFunction(isEditing: boolean) {
    return (id: number, tagIds: number[]) =>
      isEditing ? this.packagingSystemApiService.setPackagingSystemTags(id, tagIds) : this.packagingSystemApiService.addPackagingSystemTags(id, tagIds);
  }

  getPackagingPartFunction() {
    return (id: number) => this.packagingSystemApiService.getPackagingSystem(id);
  }

  resetSelection() {
    this.selectedPackagingSystemIds = [];
    this.allItemsSelected = false;
    this.packagingPartsSelected.emit(this.selectedPackagingSystemIds);
  }

  downloadPackagingSystemsJson() {
    const packagingSystemsToDownload: { id: number; version: number }[] = [];
    this.selectedPackagingSystemIds.forEach(id => {
      if (id != null) {
        packagingSystemsToDownload.push({ id: id, version: 0 });
      }
    });
    this.jsonSubscription = this.packagingSystemApiService.downloadPackagingSystemsJson(packagingSystemsToDownload).subscribe(zipFile => {
      const zipUrl = window.URL.createObjectURL(zipFile);
      window.open(zipUrl, '_blank', 'noreferrer');
    });
  }

  private getDeleteSuccessData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.success'),
      messages: [this.translateService.translate('dataManagement.directory.dialog.removal.packagingSystemsDeletedSuccess')],
      icon: 'info',
    };
  }

  private getDeletionImpossibleData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.error'),
      messages: [this.translateService.translate('packagingSystem.messages.packagingSystemInUse')],
      icon: 'error',
    };
  }

  private getDeleteErrorData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.error'),
      messages: [this.translateService.translate('dataManagement.directory.dialog.removal.packagingSystemsDeletedError')],
      icon: 'error',
    };
  }

  onPageChanged(event: PageEvent) {
    if (this.dataSource instanceof PaginationDataSource) {
      this.dataSource.fetchData(event.pageIndex, event.pageSize);
    }
  }

  onSortChanged(event: Sort) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (event.direction) {
        this.dataSource.sort(this.getPaginationFieldName(event.active), event.direction == 'asc');
      } else {
        this.dataSource.removeSorting();
      }
    }
  }

  getPaginationFieldName(column: string) {
    switch (column) {
      case 'id':
        return PackagingSystemPaginationFields.Id;
      case 'tags':
        return PackagingSystemPaginationFields.Tag;
      case 'brandName':
        return PackagingSystemPaginationFields.BrandName;
      case 'productName':
        return PackagingSystemPaginationFields.ProductName;
      case 'articleNumber':
        return PackagingSystemPaginationFields.ArticleNumber;
      case 'directoryName':
        return PackagingSystemPaginationFields.DirectoryId;
      case 'supplier':
        return PackagingSystemPaginationFields.Supplier;
    }

    return '';
  }

  getStatusOptions(): { name: string; value: string }[] {
    if (!this.subRequestMap) {
      return [];
    }
    const labels = [...this.subRequestMap.values()].map(s => this.translateService.translate(this.getSubRequestStatusLabel(s.status))).filter(Boolean) as string[];
    return [...new Set(labels)].sort().map(label => ({ name: label, value: label }));
  }

  getCaller(columnName: string) {
    if (this.isPaginatorVisible) {
      return this.getPaginationFieldName(columnName);
    }

    return columnName;
  }
}
